// ============================================================
// PREZENCE — Analytics page (Reports & Analytics module)
// Trend charts + comparisons over a selectable date range.
// Wired to /api/analytics/* endpoints. Admin/HR only (route-guarded).
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell,
} from 'recharts';
import api from '../../api/client';
import toast from 'react-hot-toast';
import './analytics.css';

const RANGES = [{ label: '7D', days: 7 }, { label: '30D', days: 30 }, { label: '90D', days: 90 }];
const COLORS = ['#63dcbc', '#3ba8ff', '#a78bfa', '#f59e0b', '#ef4444', '#22c55e'];
const tip = { background: '#0b1326', border: '1px solid rgba(0,219,231,.25)', borderRadius: 10, fontSize: 12, color: '#dae2fd' };
const fmtDay = d => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { day: 'numeric', month: 'short' });

function Delta({ value, invert }) {
  if (!value || value === 0) return <span className="an-delta an-flat">—</span>;
  const good = invert ? value < 0 : value > 0;
  return <span className={`an-delta ${good ? 'an-up' : 'an-down'}`}>{value > 0 ? '▲' : '▼'} {Math.abs(value)}{invert ? '%' : ''}</span>;
}

export default function Analytics() {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [prodTrend, setProdTrend] = useState([]);
  const [attTrend, setAttTrend] = useState([]);
  const [depts, setDepts] = useState([]);
  const [leaveUtil, setLeaveUtil] = useState([]);
  const [performers, setPerformers] = useState({ top: [], bottom: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, pt, at, dp, lu, pf] = await Promise.all([
        api.get(`/analytics/overview?days=${days}`),
        api.get(`/analytics/productivity-trend?days=${days}`),
        api.get(`/analytics/attendance-trend?days=${days}`),
        api.get(`/analytics/departments?days=${days}`),
        api.get(`/analytics/leave-utilization`),
        api.get(`/analytics/top-performers?days=${days}`),
      ]);
      setOverview(ov.data.kpis);
      setProdTrend(pt.data.trend || []);
      setAttTrend(at.data.trend || []);
      setDepts(dp.data.departments || []);
      setLeaveUtil(lu.data.utilization || []);
      setPerformers({ top: pf.data.top || [], bottom: pf.data.bottom || [] });
    } catch (e) {
      toast.error('Failed to load analytics');
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const kpis = overview || {};

  return (
    <div className="an-root">
      <div className="an-header">
        <div>
          <h1 className="an-title">Analytics</h1>
          <p className="an-sub">Workforce trends and insights</p>
        </div>
        <div className="an-range">
          {RANGES.map(r => (
            <button key={r.days} className={`an-range-btn ${days === r.days ? 'active' : ''}`} onClick={() => setDays(r.days)}>{r.label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="an-loading"><div className="spinner" /></div>
      ) : (
        <>
          {/* KPI row */}
          <div className="an-kpis">
            <div className="an-kpi">
              <div className="an-kpi-label">AVG PRODUCTIVITY</div>
              <div className="an-kpi-val" style={{ color: '#63dcbc' }}>{kpis.avgProductivity ?? 0}%</div>
              <Delta value={kpis.productivityDelta} />
            </div>
            <div className="an-kpi">
              <div className="an-kpi-label">LATE RATE</div>
              <div className="an-kpi-val" style={{ color: '#f59e0b' }}>{kpis.lateRate ?? 0}%</div>
              <Delta value={kpis.lateRateDelta} invert />
            </div>
            <div className="an-kpi">
              <div className="an-kpi-label">ACTIVE HOURS</div>
              <div className="an-kpi-val" style={{ color: '#3ba8ff' }}>{kpis.totalActiveHours ?? 0}</div>
              <span className="an-delta an-flat">this period</span>
            </div>
            <div className="an-kpi">
              <div className="an-kpi-label">ACTIVE VS IDLE</div>
              <div className="an-kpi-val" style={{ color: '#a78bfa' }}>{kpis.activeVsIdle ?? 0}%</div>
              <span className="an-delta an-flat">active share</span>
            </div>
          </div>

          {/* Productivity trend */}
          <div className="an-panel">
            <div className="an-panel-title">PRODUCTIVITY TREND</div>
            {prodTrend.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={prodTrend} margin={{ left: -18, right: 8, top: 8 }}>
                  <CartesianGrid stroke="rgba(255,255,255,.04)" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fill: '#475569', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fill: '#475569', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} />
                  <Tooltip contentStyle={tip} formatter={(v, n) => [n === 'productivity' ? `${v}%` : `${v}h`, n]} labelFormatter={fmtDay} />
                  <Line type="monotone" dataKey="productivity" stroke="#63dcbc" strokeWidth={2.5} dot={{ r: 3, fill: '#63dcbc' }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="an-grid2">
            {/* Attendance trend */}
            <div className="an-panel">
              <div className="an-panel-title">ATTENDANCE — PRESENT VS LATE</div>
              {attTrend.length === 0 ? <Empty /> : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={attTrend} margin={{ left: -22, right: 4, top: 8 }}>
                    <CartesianGrid stroke="rgba(255,255,255,.04)" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fill: '#475569', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#475569', fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={tip} labelFormatter={fmtDay} cursor={{ fill: 'rgba(0,219,231,.04)' }} />
                    <Bar dataKey="present" stackId="a" fill="#22c55e" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="late" stackId="a" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Leave utilization */}
            <div className="an-panel">
              <div className="an-panel-title">LEAVE UTILIZATION (THIS YEAR)</div>
              {leaveUtil.length === 0 ? <Empty msg="No approved leave yet" /> : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={leaveUtil} dataKey="days" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3}>
                      {leaveUtil.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} strokeWidth={0} />)}
                    </Pie>
                    <Tooltip contentStyle={tip} formatter={v => [`${v} days`, '']} />
                  </PieChart>
                </ResponsiveContainer>
              )}
              {leaveUtil.length > 0 && (
                <div className="an-legend">
                  {leaveUtil.map((l, i) => (
                    <span key={i}><i style={{ background: COLORS[i % COLORS.length] }} />{l.name} ({l.days}d)</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Department comparison */}
          <div className="an-panel">
            <div className="an-panel-title">DEPARTMENT COMPARISON</div>
            {depts.length === 0 ? <Empty /> : (
              <ResponsiveContainer width="100%" height={Math.max(180, depts.length * 42)}>
                <BarChart data={depts} layout="vertical" margin={{ left: 30, right: 20 }}>
                  <CartesianGrid stroke="rgba(255,255,255,.04)" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} tick={{ fill: '#475569', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} />
                  <YAxis type="category" dataKey="department" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} width={120} />
                  <Tooltip contentStyle={tip} cursor={{ fill: 'rgba(0,219,231,.04)' }} formatter={(v, n) => [n === 'avgProductivity' ? `${v}%` : `${v}%`, n === 'avgProductivity' ? 'Productivity' : 'Late rate']} />
                  <Bar dataKey="avgProductivity" fill="#63dcbc" radius={[0, 4, 4, 0]} barSize={16} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Performers */}
          <div className="an-grid2">
            <div className="an-panel">
              <div className="an-panel-title" style={{ color: '#22c55e' }}>TOP PERFORMERS</div>
              <PerformerList list={performers.top} accent="#22c55e" />
            </div>
            <div className="an-panel">
              <div className="an-panel-title" style={{ color: '#f59e0b' }}>NEEDS ATTENTION</div>
              <PerformerList list={performers.bottom} accent="#f59e0b" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function PerformerList({ list, accent }) {
  if (!list || list.length === 0) return <Empty msg="Not enough data yet" />;
  return (
    <div className="an-performers">
      {list.map((p, i) => (
        <div className="an-performer" key={i}>
          <div className="an-perf-rank" style={{ color: accent }}>{i + 1}</div>
          <div className="an-perf-info">
            <div className="an-perf-name">{p.name}</div>
            <div className="an-perf-dept">{p.department || '—'}</div>
          </div>
          <div className="an-perf-score" style={{ color: accent }}>{p.avgProductivity}%</div>
        </div>
      ))}
    </div>
  );
}

function Empty({ msg = 'Not enough data for this period yet' }) {
  return <div className="an-empty">{msg}</div>;
}
